/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';

/**
 * Acima checkout button — Client Component.
 *
 * Responsibilities:
 *   1. Load the Acima JS SDK script lazily (not blocking LCP) on first user interaction.
 *   2. Fetch the Acima transaction payload via the local resource route
 *      GET /action/acima-checkout-details (server proxies SCAPI — no browser CORS).
 *   3. Open the Acima-hosted lease-application iframe using the SDK.
 *   4. Handle the iframe approval callback: POST LeaseId / LeaseNumber / CheckoutToken
 *      via the local resource route POST /action/acima-lease (server proxies SCAPI).
 *
 * No direct browser → SCAPI calls. All SCAPI traffic goes through the SFNext server.
 */

declare global {
    interface Window {
        // Acima JS SDK exposed global — set by the AcimaJsFileUrl script.
        // Real SDK shape (legacy ISML + sandbox + production):
        //   new window.Acima.Client({ merchantId, iframeUrl, iframeContainer })
        //     .checkout({ transaction })
        //     .then(({ leaseId, leaseNumber, checkoutToken }) => ...)
        //     .catch(({ code, message }) => ...)
        //   client.closeIframe()  // resets the SDK's internal open-state (see below)
        Acima?: {
            Client: new (options: {
                merchantId: string;
                iframeUrl: string;
                iframeContainer: HTMLElement;
            }) => AcimaClient;
        };
        // Global "iframe open" guard the Acima launcher sets in _openIframe and only
        // clears in _closeIframe. If the iframe DOM is torn down without calling
        // closeIframe(), this stays true and every subsequent checkout() throws
        // "Iframe already open or improperly closed". We clear it defensively.
        acimaOpenIframe?: boolean;
    }
}

/** Minimal shape of an Acima SDK Client instance that we depend on. */
interface AcimaClient {
    checkout: (options: { transaction: unknown }) => Promise<AcimaApprovalResult>;
    /** Properly tears down the iframe and resets window.acimaOpenIframe. */
    closeIframe: () => void;
}

/** Lowercase keys — matches the actual Acima SDK callback shape. */
export interface AcimaApprovalResult {
    leaseId: string;
    leaseNumber: string;
    checkoutToken: string;
}

/**
 * DEV-only lifecycle logger for the Acima checkout flow. Signing a real lease is
 * slow and hard to reproduce, so we trace every step (button click → checkout
 * details → iframe open → approval → lease POST → outcome) in the parent-window
 * console. `import.meta.env.DEV` strips all of this from production builds — no
 * lease identifiers or flow internals are ever logged in prod.
 */
function acimaDevLog(step: string, detail?: Record<string, unknown>): void {
    if (!import.meta.env.DEV) return;
    // eslint-disable-next-line no-console
    console.log(`[Acima DEV] ${step}`, detail ?? '');
}

export interface AcimaCheckoutButtonProps {
    /** AcimaMerchantId from server-side config — non-sensitive but must not be a hardcoded literal. */
    merchantId: string;
    /** Acima JS SDK URL from server-side config (`AcimaJsFileUrl`). */
    jsFileUrl: string;
    /** Acima iframe base URL (`AcimaIframeUrl`). */
    iframeUrl: string;
    /**
     * NOTE: basketId is intentionally NOT a prop. The SLAS shopper session binds the
     * server-side action routes to the correct basket via the access token; passing
     * a basketId from the client would be a forgery vector. See `.cursor/rules/
     * rules-custom-scapi-ashleys.mdc` / `.claude/skills/scapi` rule 1a.
     */
    /** Called after lease data is successfully stored on the basket. */
    onLeaseApproved?: (result: AcimaApprovalResult) => void;
    /** Called on any error (fetch, SDK, or lease-store failure). */
    onError?: (message: string) => void;
    /** Called when shopper cancels the Acima iframe. */
    onCancel?: () => void;
    className?: string;
    /**
     * Button label. Defaults to the localized "Sign Lease" string — used at the
     * place-order step after Acima is selected as the payment method (matches
     * legacy SFRA UX, where the iframe is launched from the order-summary
     * "Sign Lease" CTA).
     */
    label?: string;
    /**
     * When true, the "Sign Lease" button stays disabled until the shopper ticks
     * the Terms & Conditions checkbox (legacy parity — the order-review "Sign
     * Lease" CTA only activates after agreeing to Ashley's T&C). Defaults to
     * false so standalone/atomic usages are unaffected.
     */
    requireTermsAgreement?: boolean;
    /** Href for the Terms & Conditions link rendered next to the gate checkbox. */
    termsHref?: string;
    /**
     * Custom node for the agreement label (lets the parent inject a
     * site/locale-aware <Link> and translated copy). When omitted, a default
     * English message with a plain anchor to `termsHref` is rendered.
     */
    termsLabel?: ReactNode;
}

export default function AcimaCheckoutButton({
    merchantId,
    jsFileUrl,
    iframeUrl,
    onLeaseApproved,
    onError,
    onCancel,
    className,
    label,
    requireTermsAgreement = false,
    termsHref = '/terms-and-conditions',
    termsLabel,
}: AcimaCheckoutButtonProps) {
    const { t } = useTranslation('checkout');
    const [loading, setLoading] = useState(false);
    const [iframeOpen, setIframeOpen] = useState(false);
    // Legacy parity: the "Sign Lease" CTA only activates once the shopper agrees
    // to Ashley's Terms & Conditions.
    const [termsAgreed, setTermsAgreed] = useState(false);
    // The modal is portaled to <body> (see render) so it escapes the checkout
    // form's stacking context — otherwise PayPal's express-checkout button
    // iframes composite above the in-form overlay and bleed through. Portaling
    // is only safe after mount (no `document` during SSR).
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    // DOM node where the Acima SDK injects its iframe
    const iframeContainerRef = useRef<HTMLDivElement | null>(null);

    // Fetcher for HP-5 (GET /checkout-details) — proxied server-side, no CORS
    const detailsFetcher = useFetcher<{ success: boolean; transactionData: unknown; error?: string }>();
    // Fetcher for HP-6 (POST /lease) — proxied server-side, no CORS
    const leaseFetcher = useFetcher<{ success: boolean; error?: string }>();

    // Pending approval result waiting for HP-6 to complete
    const pendingApprovalRef = useRef<AcimaApprovalResult | null>(null);

    // Monotonic session id. Each iframe open captures the current value; the
    // SDK's .then/.catch compare against it and bail if it no longer matches.
    // This prevents a stale promise from a previous (closed) Client instance —
    // which can settle late with "iframe already open / improperly closed" —
    // from clobbering the session the shopper just re-opened. Closing the modal
    // also bumps this so the discarded promise's late settlement is a no-op.
    const sessionRef = useRef(0);

    // The live Acima Client instance for the currently open iframe. Kept so the
    // shopper-initiated close can call client.closeIframe(), which is the SDK's
    // only way to reset window.acimaOpenIframe and detach the message listener.
    const clientRef = useRef<AcimaClient | null>(null);

    const loadSdk = useCallback((): Promise<void> => {
        if (window.Acima?.Client) return Promise.resolve();
        return new Promise((resolve, reject) => {
            // A stale <script> may linger if a previous load was interrupted; drop it
            // before re-adding so the SDK re-executes cleanly.
            document.querySelector(`script[src="${jsFileUrl}"]`)?.remove();
            const script = document.createElement('script');
            script.src = jsFileUrl;
            script.async = true;
            script.onload = () => {
                if (window.Acima?.Client) resolve();
                else reject(new Error('Acima SDK loaded but did not expose window.Acima.Client'));
            };
            script.onerror = () => reject(new Error('Failed to load Acima SDK'));
            document.head.appendChild(script);
        });
    }, [jsFileUrl]);

    const openIframe = useCallback(
        async (transactionData: unknown) => {
            try {
                await loadSdk();
            } catch (err) {
                onError?.(err instanceof Error ? err.message : 'Failed to load Acima SDK');
                setLoading(false);
                return;
            }

            if (!window.Acima?.Client) {
                onError?.('Acima SDK did not expose window.Acima.Client');
                setLoading(false);
                return;
            }

            // The iframe container is always rendered (toggled via CSS), so the ref is ready.
            const container = iframeContainerRef.current;
            if (!container) {
                onError?.('Acima iframe container failed to mount');
                setLoading(false);
                return;
            }
            // Defensive reset: if a prior session was torn down without the SDK's own
            // closeIframe() running (e.g. an interrupted load), window.acimaOpenIframe
            // can still be true and would make checkout() throw "already open". A fresh
            // Client gets a fresh launcher, so only the global flag + orphan DOM matter.
            container.innerHTML = '';
            window.acimaOpenIframe = false;
            // Capture the session this Client instance belongs to. If the shopper
            // closes and re-opens before this promise settles, sessionRef advances
            // and the handlers below short-circuit instead of touching the new session.
            const session = (sessionRef.current += 1);
            setIframeOpen(true);
            acimaDevLog('SDK loaded — opening lease-application iframe', { merchantId, iframeUrl, session });

            const client = new window.Acima.Client({
                merchantId,
                iframeUrl,
                iframeContainer: container,
            });
            clientRef.current = client;
            client
                .checkout({ transaction: transactionData })
                .then((result) => {
                    // Ignore a resolution from a Client whose modal was already closed/replaced.
                    if (session !== sessionRef.current) {
                        acimaDevLog('iframe .checkout() resolved for a stale session — ignoring', { session });
                        return;
                    }
                    acimaDevLog('iframe .checkout() resolved (approval callback fired)', {
                        leaseId: result.leaseId ? 'present' : 'MISSING',
                        leaseNumber: result.leaseNumber ? 'present' : 'MISSING',
                        checkoutToken: result.checkoutToken ? 'present' : 'MISSING',
                    });
                    // DEV-ONLY capture aid: the Acima iframe blocks interaction while
                    // DevTools is open, so the Network tab can't be used to read the
                    // approval payload. The console buffers messages logged while DevTools
                    // is closed and shows them on open, so log the lease identifiers here.
                    // `import.meta.env.DEV` strips this from production builds — lease
                    // identifiers must never be logged in prod.
                    if (import.meta.env.DEV) {
                        // eslint-disable-next-line no-console
                        console.log('[Acima DEV] iframe approval result — copy into Postman env:', {
                            acima_lease_id: result.leaseId,
                            acima_lease_number: result.leaseNumber,
                            acima_checkout_token: result.checkoutToken,
                        });
                    }
                    pendingApprovalRef.current = result;
                    // Approval succeeded — mark THIS session as settled by advancing sessionRef.
                    // Some Acima SDK builds are not real Promises: when we later call
                    // client.closeIframe() from the leaseFetcher useEffect, the SDK can also
                    // fire a synthetic `.catch("Iframe closed…")` on the same call. Without
                    // this bump the catch below would still see `session === sessionRef.current`
                    // and clobber the just-approved state with `onError(...)` — surfacing the
                    // red "…encountered an error" banner AFTER a successful lease. Bumping
                    // makes the late catch bail out via the stale-session guard.
                    sessionRef.current += 1;
                    acimaDevLog('POST /action/acima-lease — storing lease data on basket');
                    // POST lease data through the SFNext server action (no direct SCAPI from browser).
                    // basketId is NOT sent — the SLAS session binds the basket on the server side.
                    void leaseFetcher.submit(
                        JSON.stringify({
                            leaseId: result.leaseId,
                            leaseNumber: result.leaseNumber,
                            checkoutToken: result.checkoutToken,
                        }),
                        { method: 'POST', action: '/action/acima-lease', encType: 'application/json' }
                    );
                })
                .catch((err: { code?: string; message?: string } | unknown) => {
                    // Promise-based SDK conflates cancel + error in one catch path.
                    const rawMsg =
                        err && typeof err === 'object' && 'message' in err
                            ? (err as { message?: string }).message
                            : undefined;
                    const msg = typeof rawMsg === 'string' && rawMsg ? rawMsg : 'Acima checkout encountered an error';
                    // The SDK can leave itself in a transient "iframe already open /
                    // improperly closed" state — recoverable, not a real checkout failure.
                    const isStaleIframeState = /improperly closed|already open|iframe.*closed/i.test(msg);

                    // A rejection from a Client whose modal was already closed/replaced
                    // must be a complete no-op. A newer session (re-open OR a successful
                    // approval that advanced sessionRef above) owns the current
                    // window.Acima/iframe, so touching anything here — including purging the
                    // SDK — would tear down the live session. handleClose / the next open
                    // already handle SDK re-initialization.
                    if (session !== sessionRef.current) {
                        acimaDevLog('iframe .checkout() rejected for a stale session — ignoring', {
                            session,
                            message: msg,
                        });
                        return;
                    }

                    // Belt-and-suspenders: if the approval already resolved for this same
                    // session (pendingApprovalRef set) and the SDK is now firing a
                    // synthetic close/error, do not clobber the approval — the leaseFetcher
                    // effect owns teardown. This guards against SDK builds that don't
                    // implement `.then`/`.catch` on a real Promise.
                    if (pendingApprovalRef.current) {
                        acimaDevLog(
                            'iframe .checkout() rejected AFTER approval was captured — ignoring stale SDK error',
                            { message: msg }
                        );
                        return;
                    }

                    acimaDevLog('iframe .checkout() rejected (cancel or error)', {
                        code:
                            err && typeof err === 'object' && 'code' in err
                                ? (err as { code?: string }).code
                                : undefined,
                        message: msg,
                    });
                    // Tear down through the SDK so window.acimaOpenIframe is reset and the
                    // message listener detached; fall back to manual cleanup if needed.
                    clientRef.current?.closeIframe();
                    clientRef.current = null;
                    if (container) container.innerHTML = '';
                    window.acimaOpenIframe = false;
                    setLoading(false);
                    setIframeOpen(false);
                    if (isStaleIframeState) {
                        // Recoverable internal SDK state, not a real failure — treat as a
                        // benign cancel rather than surfacing a scary error to the shopper.
                        onCancel?.();
                        return;
                    }
                    onError?.(msg);
                });
        },
        [merchantId, iframeUrl, loadSdk, leaseFetcher, onError, onCancel]
    );

    // True only when a user click is awaiting its checkout-details response. We consume
    // this in the effect below to distinguish "user just clicked Sign Lease" from
    // "React Router auto-revalidated the fetcher after leaseFetcher.submit". After a
    // successful lease submit, React Router re-runs every fetcher.load that has data —
    // detailsFetcher gets a fresh data reference, which would otherwise re-fire
    // openIframe on a button that is unmounting (onLeaseApproved just flipped the
    // parent's `acimaLeaseApproved` flag), read a null iframeContainerRef.current, and
    // surface a spurious "Acima iframe container failed to mount" error alongside the
    // "lease approved" success message.
    const pendingUserClickRef = useRef(false);

    // When HP-5 (checkout-details) resolves in response to a user click, open the iframe.
    // Revalidation-triggered re-fetches (pendingUserClickRef=false) are ignored.
    useEffect(() => {
        if (detailsFetcher.state !== 'idle' || !detailsFetcher.data) return;
        if (!pendingUserClickRef.current) return;
        pendingUserClickRef.current = false;
        if (!detailsFetcher.data.success || !detailsFetcher.data.transactionData) {
            acimaDevLog('GET /action/acima-checkout-details FAILED', {
                success: detailsFetcher.data.success,
                error: detailsFetcher.data.error,
            });
            onError?.(detailsFetcher.data.error ?? 'Failed to fetch Acima checkout details');
            setLoading(false);
            return;
        }
        acimaDevLog('GET /action/acima-checkout-details OK — transaction payload received');
        void openIframe(detailsFetcher.data.transactionData);
    }, [detailsFetcher.state, detailsFetcher.data, openIframe, onError]);

    // When HP-6 (lease) resolves, notify parent and close the iframe
    useEffect(() => {
        if (leaseFetcher.state !== 'idle' || !leaseFetcher.data) return;
        const approval = pendingApprovalRef.current;
        pendingApprovalRef.current = null;
        if (!leaseFetcher.data.success) {
            acimaDevLog('POST /action/acima-lease FAILED — lease NOT stored on basket', {
                error: leaseFetcher.data.error,
            });
            onError?.(leaseFetcher.data.error ?? 'Failed to store lease data');
        } else if (approval) {
            acimaDevLog('POST /action/acima-lease OK — lease stored; ready to place order');
            onLeaseApproved?.(approval);
        }
        // The SDK does not auto-close its iframe after a successful checkout, so tear
        // it down here to reset window.acimaOpenIframe for any subsequent attempt.
        clientRef.current?.closeIframe();
        clientRef.current = null;
        if (iframeContainerRef.current) iframeContainerRef.current.innerHTML = '';
        window.acimaOpenIframe = false;
        setLoading(false);
        setIframeOpen(false);
    }, [leaseFetcher.state, leaseFetcher.data, onLeaseApproved, onError]);

    const handleClick = useCallback(() => {
        setLoading(true);
        pendingUserClickRef.current = true;
        acimaDevLog('Sign Lease clicked — requesting GET /action/acima-checkout-details');
        // Fetch transactionData through the SFNext server loader (no direct SCAPI from browser).
        // Basket is resolved from the SLAS session server-side — no basketId in the URL.
        void detailsFetcher.load('/action/acima-checkout-details');
    }, [detailsFetcher]);

    // Explicit close of the modal (× button / Esc). Closing via the SDK's own
    // closeIframe() is essential: it resets window.acimaOpenIframe and detaches the
    // message listener. Tearing down the DOM manually (without closeIframe) leaves
    // the global flag set and makes the NEXT checkout() throw "iframe already open".
    const handleClose = useCallback(() => {
        acimaDevLog(
            'Modal closed explicitly (×/Esc) — calling SDK closeIframe(). NOTE: if the approval callback had not yet fired, the lease was NOT stored.'
        );
        // Advance the session so the in-flight Client's promise (if it settles
        // late) becomes a no-op instead of clobbering a subsequent re-open.
        sessionRef.current += 1;
        clientRef.current?.closeIframe();
        clientRef.current = null;
        const container = iframeContainerRef.current;
        if (container) container.innerHTML = '';
        // Belt-and-suspenders in case the launcher had not yet loaded when closeIframe
        // ran (the queued checkout could still open an iframe afterwards).
        window.acimaOpenIframe = false;
        pendingApprovalRef.current = null;
        setIframeOpen(false);
        setLoading(false);
        onCancel?.();
    }, [onCancel]);

    // Close the modal on Escape while it is open.
    useEffect(() => {
        if (!iframeOpen) return undefined;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [iframeOpen, handleClose]);

    const isLoading = loading || detailsFetcher.state !== 'idle' || leaseFetcher.state !== 'idle';
    const termsGateBlocks = requireTermsAgreement && !termsAgreed;

    return (
        <>
            {requireTermsAgreement && (
                <div className="mb-2 flex items-start gap-2">
                    <input
                        type="checkbox"
                        id="acima-terms-agree"
                        checked={termsAgreed}
                        onChange={(e) => setTermsAgreed(e.target.checked)}
                        className="mt-0.5 size-4 shrink-0 accent-primary"
                    />
                    <label htmlFor="acima-terms-agree" className="text-xs leading-5 text-muted-foreground">
                        {termsLabel ?? (
                            <>
                                {t('payment.acima.termsPrompt')}{' '}
                                <a
                                    href={termsHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-foreground">
                                    {t('payment.acima.termsLink')}
                                </a>
                                .
                            </>
                        )}
                    </label>
                </div>
            )}
            <button
                type="button"
                onClick={handleClick}
                disabled={isLoading || termsGateBlocks}
                aria-busy={isLoading}
                className={className}>
                {isLoading ? t('payment.acima.loading') : (label ?? t('payment.acima.signLeaseDefault'))}
            </button>
            {/*
             * Acima iframe modal. The container ref is always mounted (visibility
             * toggled via CSS) so it exists when the SDK injects the iframe. When
             * open it renders as a centered overlay above a dimmed backdrop —
             * matching the legacy order-review "Sign Lease" modal.
             *
             * Portaled to <body> so the fixed overlay escapes the checkout form's
             * stacking context; without the portal, PayPal's express-checkout
             * button iframes (a separate compositing layer) render on top of it.
             */}
            {mounted &&
                createPortal(
                    <div
                        role={iframeOpen ? 'dialog' : undefined}
                        aria-modal={iframeOpen ? true : undefined}
                        aria-label={t('payment.acima.dialogLabel')}
                        // Max z-index: PayPal's express-checkout button iframes composite in their
                        // own layer with a z-index above the app's `z-50` scale, so the overlay must
                        // sit at the maximum to cover them once portaled to the root stacking context.
                        style={iframeOpen ? { zIndex: 2147483647 } : undefined}
                        className={
                            iframeOpen ? 'fixed inset-0 flex items-center justify-center bg-black/50 p-4' : 'hidden'
                        }>
                        {/*
                         * Backdrop clicks intentionally do NOT close the modal — an
                         * accidental click outside the panel must not abandon an
                         * in-progress lease application. The shopper closes explicitly
                         * via the × button (or Escape).
                         */}
                        <div className="relative flex h-[90vh] w-full max-w-2xl flex-col overflow-hidden bg-card shadow-xl">
                            {iframeOpen && (
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    aria-label={t('payment.acima.close')}
                                    className="absolute right-2 top-2 z-10 flex size-8 items-center justify-center text-2xl leading-none text-muted-foreground hover:text-foreground">
                                    ×
                                </button>
                            )}
                            <div
                                ref={iframeContainerRef}
                                data-testid="acima-iframe-container"
                                className="w-full flex-1 overflow-auto [&_iframe]:block [&_iframe]:!h-full [&_iframe]:!min-h-[1200px] [&_iframe]:w-full [&_iframe]:border-0"
                            />
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
}
